import {
  config,
  createSystem,
  defaultConfig,
  defineConfig,
  defineRecipe,
  defineSlotRecipe,
  defineTextStyles,
} from '@enechain/ecloud-designsystem'

import { STICKY_ACTION_SHADOW_GLOBAL_CSS } from '~/components/common/MasterDataTable/stickyActionColumn'
import { typographyTextStyleValues } from '~/constants/typography'

// design CSS の `--eclair-font-family` (= design/css/tokens.css) と整合させる。
// 日本語 glyph rendering を design と揃えるため、 chakra default の font stack を
// Hiragino-first stack で上書きする。
const ECLAIR_FONT_FAMILY =
  '"Hiragino Kaku Gothic Pro", "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, "Helvetica Neue", Arial, sans-serif'

// design CSS の `--eclair-font-family-mono` (UI 辞書 dictionary/fonts.md「フォントファミリ」、DS準拠)。
// DS 0.14.2 は fonts token を持たないため、 無いと `fontFamily="mono"` が Chakra 既定の stack
// (SFMono-Regular, Menlo, Monaco, Consolas, …) に落ちる。
// tooltip 本文の行間 (14px で 22.4px)。 recipe と globalCss の両方で使う (下の tooltip recipe の注記)。
const TOOLTIP_LINE_HEIGHT = '1.6'

const ECLAIR_FONT_FAMILY_MONO =
  '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Roboto Mono", "Courier New", monospace'

// 役割ベース typography の textStyle 化 (二層方式、D-1 論点1)。
// `~/constants/typography.ts` の canonical 役割定義 (`typographyTextStyleValues`)
// を chakra の `Token<TextStyle>` 形 (`{ value: {...} }`) にラップするだけで、
// 値そのものは typography.ts の単一 source から導出される。
const textStyles = defineTextStyles(
  Object.fromEntries(
    Object.entries(typographyTextStyleValues).map(([name, value]) => [
      name,
      { value },
    ]),
  ),
)

/**
 * DS textStyle の font-size を解決できる token に差し替える補正 (今は `textMd` だけ)。
 *
 * DS 0.14.2 の textStyle は font-size を単位なしの文字列 (`textMd` は `"14"`) で持ち、
 * chakra がそのまま `font-size:14;` を出すので宣言ごと捨てられる (下の globalCss の
 * DateRangePicker の注記と同じ穴)。 `textStyle="textMd"` を指定した要素 (DS の Alert・
 * dialog の content・menu の item・TextArea・Badge 等) は補正が無いと font-size を持たず、 親の値を継承する。
 * 値は UI 辞書 dictionary/fonts.md の `textMd` = 14px / 400 (ecloud `fontSizes.md` = 14px)。
 *
 * ほかの textStyle (`textSm` 12px・`headerMd` 16px 等) は直さない。 tooltip の recipe のように
 * 解決されない前提で font-size を別に当てている箇所があり、 解決させると値がぶつかるため
 * (textSm の 12px と tooltip の 14px)。 直す時は使っている箇所ごとに確かめる。
 *
 * TODO(#12312): DS 0.14.4 で textStyle の font-size が px になり上の前提が崩れたため、
 * 本補正と {@link dsFallbackFixRecipes} の要否を棚卸しする。
 */
const DS_TEXT_STYLE_SIZE_FIX: Partial<Record<string, { fontSize: string }>> = {
  textMd: { fontSize: 'md' },
}

/**
 * DS textStyle の **font stack だけ** を app の Hiragino-first へ揃える補正。
 *
 * DS (`@enechain/ecloud-designsystem`) の textStyle は 16 種すべてが
 * `"Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", ...` という**欧文先頭**の
 * stack を持つ。 DS component の recipe がこれを参照すると、 その宣言は
 * **`@layer` の外**に出るため、 `tokens.fonts.body` と `globalCss` の
 * `html, body` に入れた Hiragino-first stack (どちらも layer 内) に**常に勝つ**。
 *
 * 結果、 祖先が Hiragino-first なのに DS component の中だけ Helvetica-first になり、
 * **1 つの要素の中で欧文が Helvetica Neue・和文が Hiragino ProN** と別書体で描かれる。
 * 同じ `font-weight` でも太さの印象が揃わず、 周囲のテキスト (Hiragino **Pro**) とも
 * 書体が食い違う (デザイナー指摘 2026-09-16「ワークフローのタグで weight が日本語と
 * 英語で違うように見える」= 実体は weight ではなく書体の差)。
 *
 * `base` や `variants` に `fontFamily` を置いても `@layer recipes` に入って負けるため
 * (2026-09-16 実測)、 **textStyle 側を同名で上書きする**のが効く経路。 寸法・ウェイト・
 * letter-spacing は DS の値をそのまま使いたいので、 **font stack だけ**を差し替える。
 *
 * 対象は実測で描画に現れたものに限る (全 16 種を無条件に足すと、 未使用の定義まで
 * app の責務にしてしまう)。 2026-09-16 実測 (ワークフロー一覧 / スポット入札 /
 * 需要想定): `textMd` が大半、 次いで `headerMd`。
 *
 * **`textMd` だけは font-size も足す** ({@link DS_TEXT_STYLE_SIZE_FIX})。
 */
const dsTextStyleFontFix = Object.fromEntries(
  [
    'textMd',
    'textSm',
    'textLg',
    'textLabel',
    'textCaption',
    'textButton',
    'headerMd',
    'headerSm',
    'headerXs',
  ].map((name) => [
    name,
    {
      value: {
        fontFamily: ECLAIR_FONT_FAMILY,
        ...DS_TEXT_STYLE_SIZE_FIX[name],
      },
    },
  ]),
)

/**
 * DS component が内部で参照する `textStyle="textXx"` が本 app の system では解決されず、
 * chakra 既定値で描画される問題への補正 (2026-07-30 dev 実測)。
 *
 * 実測した乖離:
 * - checkbox の label: DS recipe は `textStyle: 'textMd'` (14px/400) 指定 → 実際は
 *   12px/500 (chakra 既定)。design プロト `.ecl-checkbox` の実測も 14px/400
 * - checkbox の control 枠: DS recipe は `Border.midEmphasis` (#d9d9d9) 指定 → 実際は
 *   chakra 既定の `border.emphasized` (#d4d4d8)。hover 枠 (`Hover.highEmphasis`) も未適用
 * - tooltip の文字 (DS 0.14.2 時点): `ui/tooltip` の `textStyle="textSm"` は font-size が
 *   単位なしで捨てられ、 chakra 既定 tooltip recipe の `xs` (10px) が出ていた。 0.14.4 以降は
 *   textSm (12px) が style prop (`@layer utilities`) で効いて recipe に勝つため、 下の tooltip
 *   recipe は `md!` で上書きする
 *
 * checkbox の補正は、 DS の textStyle 群が本 app の system でも解決されれば不要になる (#12312)。
 * tooltip の補正は、 DS の `ui/tooltip` が contentProps の後で textStyle を渡す限り外せない。
 */
const dsFallbackFixRecipes = {
  checkbox: defineSlotRecipe({
    slots: ['root', 'label', 'control', 'indicator', 'group'],
    base: {
      // 未チェックの枠は DS 定義どおり Border.midEmphasis、hover で Hover.highEmphasis
      // (design/ui-feasibility/components/checkbox.md の状態一覧が正)。
      control: {
        // chakra 既定 recipe は borderColor を variant 側で持つため base の指定では
        // 負ける (2026-07-30 実測: base 指定のみでは #d4d4d8 のまま)。 `:not()` で
        // specificity を 1 段上げて勝たせる。 checked / indeterminate は borderWidth 0
        // なので枠色の指定は影響しない。
        '&:not([data-disabled])': { borderColor: 'Border.midEmphasis' },
        _hover: { borderColor: 'Hover.highEmphasis' },
      },
      // DS 定義は textStyle textMd (14px / 400)。
      label: { fontSize: 'md', fontWeight: 'normal' },
    },
    // control ↔ label の間隔。 DS は base.root で `gap: "2"` (8px) を宣言しているが、
    // chakra 既定 recipe が `variants.size.md.root.gap = "2.5"` (10px) を持ち、
    // variant が base に勝つため DS の宣言値が出ない (上の control borderColor と同型。
    // 2026-08-24 実測)。
    // [DS 差異] 2026-08-24 デザイナー指摘「もっと開ける」。 DS の宣言値 8px・
    // 8080 の当該欄 6px より意図的に広げる。 DS spacing token に 2.5 の段が無いため、
    // 現状 (10px) より広い最初の段 spacing.3 (12px) を採る。
    variants: { size: { md: { root: { gap: '3' } } } },
  }),
  radioGroup: defineSlotRecipe({
    // chakra の `radioGroupAnatomy.keys()` と同じ並び・同じ要素数で宣言する
    // (config merge が配列を index 単位で上書きするため。 dialog recipe の注記と同旨)。
    // 2026-08-05 実測: ["root","label","item","itemText","itemControl","indicator",
    // "itemAddon","itemIndicator"]。
    slots: [
      'root',
      'label',
      'item',
      'itemText',
      'itemControl',
      'indicator',
      'itemAddon',
      'itemIndicator',
    ],
    base: {
      // DS 定義は textStyle textMd (14px)。 chakra 既定 recipe は size=md で
      // `item: { textStyle: 'sm' }` を当てるが、 本 app では textStyle が解決されず
      // ecloud の fontSizes.sm (= 12px) に落ちてラベルが 1 段小さくなる
      // (checkbox の label と同型の事象)。
      //
      // 当てる先は `item` (親) ではなく `itemText` (ラベル本体)。 chakra の指定は
      // `variants.size.md.item` にあり base の `item` では負けるが、 子要素である
      // `itemText` に直接 font-size を置けば親からの継承より強い。
      itemText: { fontSize: 'md' },
      // 未選択の枠の太さ (design 実測 2px) は globalCss 側で当てる (下記参照)。
    },
  }),
  /**
   * 表の既定 (size md) の文字・余白。 UI 辞書 components/table.md §0 の器 `.ecl-table`
   * (TD = md 14px 継承 / TD padding 6px 10px / TH padding 10px 12px)。
   *
   * DS 0.14.2 は table の recipe を持たず、 補正が無いと Chakra 既定の size md
   * (root `textStyle: 'sm'` = 12px、 cell・columnHeader の padding 12px) で描かれる。 既定 (md) だけを補正し、
   * 密度優先の size="sm" (48 コマ表・行内フォーム等) と lg は Chakra 既定のまま。
   * cell の padding を inline style / style prop で持つ表 (`MasterDataTable` 等) はそちらが勝つ。
   *
   * 見出し (TH) の地・文字色・太さと罫線の色は size によらず table.md §0 の値にする
   * (`.ecl-table th` = 地 `Surface.tableHeader` / 文字 `Text.midEmphasis` / 600、
   * TH・TD の下罫線 `Border.lowEmphasis`)。 Chakra 既定は地なし・`fg` (#09090b)・500・
   * 罫線 #e4e4e7 なので、 見出しを自前で塗っていない表 (時間前の入札一覧・入札制限の表 等) は
   * 補正が無いと 8080 と別の見た目になる (2026-10-19 実測)。 `TABLE_HEADER_CELL_STYLE` や prop で
   * 見出しを指定している表は、 そちらが勝つので変わらない。
   * 折り返し (`.ecl-table th` の nowrap) は入れない。 見出しを 2 行に折って狭い器に収めている表
   * (`ExecutionAreaSummaryTable` 等) があるため。
   */
  table: defineSlotRecipe({
    // chakra の `tableAnatomy.keys()` と同じ並び (config merge が配列を index 単位で上書きするため。
    // dialog recipe の注記と同旨)。 2026-10-16 実測 (@chakra-ui/react 3.29.0 `anatomy.js`)。
    slots: [
      'root',
      'header',
      'body',
      'row',
      'columnHeader',
      'cell',
      'footer',
      'caption',
    ],
    base: {
      columnHeader: {
        backgroundColor: 'Surface.tableHeader',
        color: 'Text.midEmphasis',
        fontWeight: 'semibold',
        borderColor: 'Border.lowEmphasis',
      },
      cell: { borderColor: 'Border.lowEmphasis' },
      /**
       * 縞と行 hover は、 表 (`TableRoot`) に付ける属性で入れる。 chakra 既定の variant
       * `striped` / `interactive` は奇数行・`td` 塗り・`colorPalette.subtle` で design と別物、
       * 独自の variant 名は DS の型 (`TableRootProps`) に無く渡せないため、 data 属性にした。
       *
       * - `data-zebra`     : 偶数番目の行に `Surface.secondary`
       *                      (UI 辞書 components/table.md §0 `.ecl-table tbody tr:nth-child(even)`)。
       *                      前提: 縞を付ける行だけが tbody の子 (縞は DOM の並びで決まる)。
       * - `data-row-hover` : 行 hover で `Hover.lowEmphasis` (同 `tbody tr:hover`)。
       *                      行クリックで開ける表だけに付ける (押せない行を押せるように見せない)。
       *
       * 行ごとに別の地 (売買逆転・制限違反の強調など) を style prop で当てる表は、 prop 由来の
       * 宣言が `@layer recipes` に常に勝つので、 縞・hover より強調地が優先される。
       */
      root: {
        '&[data-zebra] tbody tr:nth-of-type(even)': {
          backgroundColor: 'Surface.secondary',
        },
        '&[data-row-hover] tbody tr:hover': {
          backgroundColor: 'Hover.lowEmphasis',
        },
      },
    },
    variants: {
      size: {
        md: {
          root: { fontSize: 'md' },
          columnHeader: { px: '12px', py: '10px' },
          cell: { px: '10px', py: '6px' },
        },
      },
    },
  }),
  tooltip: defineSlotRecipe({
    slots: ['trigger', 'arrow', 'arrowTip', 'positioner', 'content'],
    base: {
      // font-size 14px / weight 400 (2026-08-06 デザイナー指摘「? アイコンにホバー
      // した際に表示する tooltip のフォントサイズは 14px」)。
      //
      // [決定の経緯] `design/ui-feasibility/components/tooltip.md` は 2026-07-28 に
      // 「Figma の 14px ではなく DS 実装値の 12px が正」と記録していたが、
      // 2026-08-06 にデザイナーが 14px に確定し直した。辞書側の更新は別 issue。
      //
      // DS の `ui/tooltip` は Content に `textStyle="textSm"` (12px) を contentProps より後ろの
      // style prop で渡す。 style prop は `@layer utilities` に出て recipe に勝ち、 contentProps
      // でも上書きできないため、 font-size だけ `!` (important) で勝たせる (DS 0.14.4 で
      // textSm の font-size が px になり効き始めた)。 line-height は textSm が持たないので不要。
      // line-height は 1.6 (UI 辞書 components/tooltip.md「行間の全画面調整（2026-10-09）」。
      // [DS差異] DS 0.14.5 の Tooltip / textSm は行間を明示しない。 1.6 は React の全画面の値で、
      // 8080 の CSS は 1.5)。
      // chakra 既定は `textStyle: 'xs'` に含まれる行間で、14px 化すると窮屈になる
      // (2026-08-06 デザイナー指摘「tooltip 内の line-height もう少し広げたい」)。
      content: {
        fontSize: 'md!',
        fontWeight: 'normal',
        lineHeight: TOOLTIP_LINE_HEIGHT,
      },
    },
  }),
}

/**
 * design プロト `.ecl-modal` の幅 (既定 `width: min(560px, 94vw)`、 `--sm` / `--lg` / `--xl`
 * = 440 / 720 / 960px) に合わせるための override。
 *
 * DS 0.14.2 は chakra 既定の dialog `size: md` (maxW = sizes.lg / 512px) を
 * `maxW: 'md'` (= 28rem / 448px) に上書きしており、design より 112px 狭い。
 * DS の `ModalView` は `Dialog.Content` に style props を通さない
 * (`ModalViewProps` は rest を `Dialog.Root` にのみ spread し、Content は内部生成) ため、
 * 幅を design に寄せられるのは slotRecipe だけ。
 */
const designParityRecipes = {
  field: defineSlotRecipe({
    // 並びは ark の `fieldAnatomy.keys()` (+ chakra が extendWith する
    // `requiredIndicator`) と一致させること。理由は下の dialog recipe の注記と同じ。
    // 2026-08-06 実測 (@ark-ui/react 5.37.2 `dist/components/field/field.anatomy.js` の
    // `createAnatomy("field").parts(...)` + @chakra-ui/react 3.29.0 `anatomy.js` の
    // `fieldAnatomy$1.extendWith("requiredIndicator")`):
    // ["root","errorText","helperText","input","label","select","textarea","requiredIndicator"]。
    slots: [
      'root',
      'errorText',
      'helperText',
      'input',
      'label',
      'select',
      'textarea',
      'requiredIndicator',
    ],
    base: {
      // design SoT: `.form-label { font-size: var(--font-size-md); font-weight: 600;
      // color: var(--text-high-emphasis) }` = 14px/600 (8080 `css/components.css` 実測)。
      // `~/constants/typography.ts` の役割 `label` (md/semibold) と同値。
      //
      // chakra 既定の field recipe は label に `textStyle: 'sm'` + `fontWeight: 'medium'`
      // (= ecloud スケールで 12px/500) を当てており、DS 側に field の override が無いため
      // フォーム項目名が design より 1 段小さく細くなる。 対象は DS から直 import している
      // 13 ファイル・90 箇所 (2026-08-06 実測)。 残り 18 ファイル・71 箇所は
      // `~/components/common/Field` 経由で、 同ラッパが per-instance に textStyle="label"
      // を当てているため元から design 準拠 (本 recipe 導入でラッパ側の指定は冗長になる)。
      // checkbox.label / radioGroup.itemText と同型の補正で、app config の merge が
      // chakra の base より後に載るため `fontSize` / `fontWeight` の明示で勝てる。
      // color は指定しない: chakra 既定 field recipe の label は色を持たず、globalCss の
      // `body { color: Text.highEmphasis }` を継承して design SoT と同色になる。base で
      // 固定すると DS が将来 invalid / disabled のラベル色を variant で持った時に干渉する。
      label: { fontSize: 'md', fontWeight: 'semibold' },
      // chakra 既定は `textStyle: 'xs'` (= ecloud スケールで 10px) + `color: 'fg.error'`
      // (chakra 標準の赤) で、文字が 1 段小さく色も DS 外。
      //
      // 色: DS `ErrorText` の実装値 `Text.negativeMid` (#d80004) を採る。
      //   8080 の `.form-error-msg` は `--text-negative-high` (#870103) だがプロト側が
      //   DS 非準拠 (上記 globalCss の [DS差異] 注記と同根)。
      // サイズ: DS `ErrorText` は `<Text color=...>` のみで font を pin しない (= 継承) ため
      //   DS 側に確定値が無い。8080 の `.form-error-msg` の 12px を採る
      //   (chakra 既定の 10px より design に近く、weight 500 は chakra と同値)。
      errorText: { fontSize: 'sm', color: 'Text.negativeMid' },
    },
  }),
  nativeSelect: defineSlotRecipe({
    // 2026-08-06 実測 (@chakra-ui/react 3.29.0 `anatomy.js` の
    // `createAnatomy("select").parts("root", "field", "indicator")`)。
    slots: ['root', 'field', 'indicator'],
    base: {
      // エラー状態の枠は DS `Input` の実装値 `Text.negativeMid` (#d80004) に揃える
      // (8080 の `--border-negative-mid` も同 hex)。
      //
      // chakra 既定 native-select recipe は `--error-color: colors.border.error`
      // (= light で `red.500` / #ef4444) を定義し、`field` の `_invalid` が枠色と
      // focus ring の双方でこれを参照する。DS 側に native-select の override は無いため
      // (実測 0 件)、そのまま chakra の赤が出る。変数を差し替えれば枠色・focus ring が
      // まとめて design 値になる (個別 override より漏れが出ない)。
      //
      // DS の `Input` / `TextArea` は component 側の inline prop で
      // `_invalid: { border: '1px solid {colors.Text.negativeMid}' }` (= #d80004、design と同値)
      // を持つため補正不要 (2026-08-06 実測)。ここで直すのは native select だけ。
      field: { '--error-color': 'colors.Text.negativeMid' },
    },
    variants: {
      size: {
        // 文字サイズ。 理由と根拠は `designParitySinglePartRecipes.input` と同じ
        // (design `.ecl-select--sm` も `font-size: var(--font-size-md)` = 14px。
        //  chakra 既定は size を問わず `textStyle: 'sm'` = 12px)。
        sm: { field: { fontSize: 'md' } },
        md: { field: { fontSize: 'md' } },
      },
    },
  }),
  dialog: defineSlotRecipe({
    // 並びは chakra の `dialogAnatomy.keys()` と一致させること。chakra の config merge は
    // 配列を index 単位で上書きするため、順序を変えると slot 名が入れ替わって壊れる。
    slots: [
      'trigger',
      'backdrop',
      'positioner',
      'content',
      'title',
      'description',
      'closeTrigger',
      'header',
      'body',
      'footer',
    ],
    base: {
      // design SoT: `.ecl-modal-title { font-size: var(--font-size-xl); font-weight: 600 }` = 20px/600
      // DS の dialog recipe は title に `textStyle: 'headerLg'` を指定するが、
      // 本 app では textStyle が未解決のまま chakra 既定に落ちる問題への補正 (dsFallbackFixRecipes と同型)。
      title: { fontSize: '20px', fontWeight: 600, color: 'Text.highEmphasis' },
    },
    variants: {
      size: {
        // content は positioner (width: 100dvw) の中で width: 100% なので、maxW に
        // min() をそのまま書けば design の `width: min(560px, 94vw)` と等価になる
        // (94vw 側が狭画面での縮小 = design と同じクランプ)。
        md: { content: { maxW: 'min(560px, 94vw)' } },
        // 幅の 4 段 (UI 辞書 dictionary/components.md §4 `.ecl-modal` の `--sm` / `--lg` / `--xl`
        // = 440 / 720 / 960px、 いずれも 94vw でクランプ)。 [DS差異] DS の dialog recipe は
        // size md しか持たず、 sm / lg / xl は chakra 既定 (448 / 672 / 896px) のままになる。
        sm: { content: { maxW: 'min(440px, 94vw)' } },
        lg: { content: { maxW: 'min(720px, 94vw)' } },
        xl: { content: { maxW: 'min(960px, 94vw)' } },
      },
    },
  }),
}

/**
 * 単一 part の recipe 版 design parity 補正 (slotRecipe と登録先が違うため分ける)。
 *
 * badge: design SoT `.badge` (8080 `css/components.css` 実測) は
 * `height: 24px / padding: 0 var(--space-2) / font-size: var(--font-size-md) /
 * font-weight: 400 / line-height: 1`。
 * DS の `Badge` は `textStyle="textMd"` (14px) を prop で渡すが、本 app では textStyle が
 * 解決されず何も出力されないため、chakra 既定 badge recipe の `size: sm`
 * (`textStyle: 'xs'` = ecloud スケールで 10px / `px: 1.5` / `minH: 5`) がそのまま出る。
 * DS `Badge` は `size` を渡さないので既定の `sm` に当たる = 一覧のステータス列が 10px になる。
 * 地色 (`Surface.<status>Mid`) は DS 側が正で dev も一致済み
 * (`design/ui-feasibility/components/badge.md` 2026-07-31 の訂正。8080 プロトの
 * `.badge-ok` が古い `--surface-*-low` のまま = プロト側の遅れ)。ここでは寸法・書体のみ直す。
 */
const designParitySinglePartRecipes = {
  /**
   * ボタンのラベルに下線を出さない。
   *
   * **[DS差異]** DS が globalCss で当てている `a:hover` の下線を、 アプリ側の
   * button recipe で打ち消す。 DS 側に「ボタンとしての `<a>`」を下線から除く口が
   * 無い (button recipe / 各 Button 実装とも `text-decoration` を宣言しない) ため、
   * 個々の呼び出し側で打ち消すか recipe で 1 本にするかの二択で、 design が
   * 全ボタン共通の 1 本として定めている以上こちらを採る。 差異の対象は
   * **`<a>` として描かれた Button の下線だけ**で、 DS の Link / bare `<a>` の
   * 下線には触れない。
   *
   * design SoT: 8080 `css/components.css` の `.btn { ... text-decoration: none }`
   * = **全ボタン共通**の規則。 8080 側に `.btn` の hover 下線ルールは 1 本も無い。
   *
   * DS は globalCss で `a { color: 'teal.500', _hover: { textDecoration: 'underline' } }`
   * を当てている (ecloud-designsystem `dist/index.es.js` の `config`)。 chakra の
   * preflight が `a { text-decoration: inherit }` で UA 既定の下線を消したうえに、
   * DS が **hover 下線だけを足し戻している**形。
   *
   * `Button` を `asChild` で `<a>` として描くと (= リンク先画面を開くボタン)
   * この規則が素通りし、 ホバーでラベルに下線が出る
   * (2026-09-03 デザイナー指摘「ホバー時にボタンラベルに下線をひかない」)。
   * DS の button recipe も各 Button 実装も `text-decoration` を一切宣言しないため、
   * 打ち消すものが無い。
   *
   * globalCss は `@layer base` / recipe は `@layer recipes` で、 layer 順は
   * reset < base < tokens < recipes。 **後の layer が常に勝つ**ので、 recipe 側で
   * 宣言すれば個々の呼び出し側に書かなくても全ボタンで止まる。 design の
   * `.btn` が全ボタン共通の 1 本である以上、 是正もここに 1 本置くのが 1:1。
   */
  button: defineRecipe({
    base: {
      textDecoration: 'none',
      // hover 側も明示する。 base の値だけでは DS globalCss の `a:hover` が
      // **より具体的な状態規則**として残り、 ホバー時だけ下線が戻るため。
      _hover: { textDecoration: 'none' },
    },
    variants: {
      variant: {
        /**
         * outline の枠を 1px `Border.midEmphasis` にする (DS `button.recipe` の outline の宣言値)。
         *
         * DS の recipe は base に `border: none`、 outline に `borderWidth: 1px` / `borderStyle: solid` /
         * `borderColor: Border.midEmphasis` を持つが、 chakra 既定の button recipe と合成されると
         * `borderWidth` / `borderColor` は chakra の base の位置 (= `border: none` より前) に出る。
         * 後ろの `border: none` が幅を `medium` (3px)・色を `currentColor` に戻し、 そのあとの
         * `borderStyle: solid` だけが効くので、 DS `IconButton variant="outline"` は **3px の黒枠**になる
         * (2026-10-19 実測)。 `border` そのものを上書きすれば `border: none` と同じ位置に出るので、
         * 宣言の順に左右されない。
         * DS の Tertiary / Secondary などの専用ボタンは枠を prop で持つので影響しない。
         */
        outline: { border: '1px solid {colors.Border.midEmphasis}' },
      },
    },
  }),
  /**
   * 入力欄の文字サイズ。
   *
   * design SoT: `.ecl-input--sm { height: 40px; font-size: var(--font-size-md) }`
   * (8080 `css/components.css`) = **14px**。 `--font-size-md: 14px` / `--font-size-sm: 12px`
   * (`design/css/tokens.css`)。
   *
   * chakra 既定の input recipe は **size を問わず `textStyle: 'sm'`** を当てており
   * (2xs〜2xl すべて sm か md か lg のいずれか、 sm/md は 'sm')、 DS スケールでは
   * 12px になる。 DS 側に input の override が無いため、 **アプリ全体の入力欄が
   * design より 1 段小さい**状態だった (2026-08-19 デザイナー指摘。 Chromium 実測で
   * 入力欄 12px / ラベル 14px)。
   *
   * 同じ問題は field recipe の label で既に是正済み (下の `designParityRecipes.field`)。
   * 入力欄本体が漏れていたので同じ方針で揃える。
   *
   * `base` ではなく size variant に置く: `textStyle` は size variant 側にあり、
   * base の `fontSize` は同じ block に merge されないため負ける。
   */
  input: defineRecipe({
    variants: {
      size: {
        sm: { fontSize: 'md' },
        md: { fontSize: 'md' },
      },
    },
  }),
  /**
   * 入力欄の文字サイズ (textarea)。 上の `input` と同根・同値。
   *
   * design SoT は 8080 `css/components.css` の `input, select, textarea` 基底
   * (`font-size: var(--font-size-md)` = 14px)。 DS `TextArea` も `textStyle="textMd"`
   * を意図しているが、 DS の textStyle は単位なしの `fontSize: "14"` なので
   * `font-size:14;` として宣言ごと捨てられ (上記 input と同じ既知の穴)、
   * chakra 既定 textarea recipe の `size: md` = 12px が残る。
   */
  textarea: defineRecipe({
    variants: {
      size: {
        sm: { fontSize: 'md' },
        md: { fontSize: 'md' },
      },
    },
  }),
  badge: defineRecipe({
    // chakra 既定の base は `fontWeight: 'medium'` (500)。app config の merge が後に載るため
    // ここでの明示で勝てる (checkbox.label と同型)。
    base: { fontWeight: 'normal', lineHeight: '1' },
    variants: {
      size: {
        // `textStyle: 'xs'` と同じ block に merge されるので、後から載る `fontSize` が勝つ。
        // 高さは `height` ではなく `minHeight` にする (= 想定外の長文が来ても文字が切れない
        // 保険。折り返し自体は globalCss の `white-space: nowrap` で止めている)。
        sm: { fontSize: 'md', minHeight: '24px', paddingInline: '2' },
      },
    },
  }),
}

// chakra v3: `createSystem(defaultConfig, config, appConfig)` で
// chakra 標準 → ecloud (DS) override → app 固有 override の順に重ねる。
// app 固有は font (Hiragino)、body の text color、役割ベース textStyles、
// DS textStyle 未解決分の補正 / design parity 補正 (上記)。
const appConfig = defineConfig({
  theme: {
    tokens: {
      fonts: {
        body: { value: ECLAIR_FONT_FAMILY },
        heading: { value: ECLAIR_FONT_FAMILY },
        mono: { value: ECLAIR_FONT_FAMILY_MONO },
      },
    },
    textStyles: { ...textStyles, ...dsTextStyleFontFix },
    recipes: { ...designParitySinglePartRecipes },
    slotRecipes: { ...dsFallbackFixRecipes, ...designParityRecipes },
  },
  globalCss: {
    // 一覧の固定 (sticky) 操作列の左辺 shadow。 `border-collapse: collapse` の表では
    // td / th への box-shadow 直当てが paint されない (issue #5489) ため、擬似要素で描く。
    // inline style には `::before` を書けないので、全テーブル共通の唯一の実体として
    // ここに置く (定義とその根拠の SoT は stickyActionColumn.ts 側)。
    ...STICKY_ACTION_SHADOW_GLOBAL_CSS,
    // 閲覧専用の表の範囲選択ハイライト (`useTableRangeCopy`、計画詳細 plan-common §2)。
    // 選択は DOM 属性で持つので、表ごとの style ではなくここに 1 度だけ置く。
    // 表のセルは inline style で背景 (zebra / 固定列) を持つため `!` (= !important) で上書きする。
    // 背景は不透明のトークン (sticky の時刻列が本文の上に浮くため、半透明だと透ける)。
    // outline は既定だと sticky の時刻列・日量合計行・見出しより手前に描かれ、スクロールで
    // それらの下へ潜ったセルの枠が透けて見える。セル自身を z-index 0 の重なりにして sticky
    // (z-index 1 以上) の下に描かせる。sticky のセルは inline の position / zIndex が勝つ。
    'td[data-range-selected], th[data-range-selected]': {
      background: 'Surface.accentPrimaryLightLow!',
      position: 'relative',
      zIndex: 0,
      outline: '1px solid',
      outlineColor: 'Border.accentPrimary',
      // -1px だとセル境界の罫線 (border-collapse で隣のセルと共有する 1px) に重なり、
      // sticky のセルの背景はその 1px を覆わないため、下へ潜ったセルの枠が罫線の位置から
      // 透けて見える (実ブラウザで確認)。境界から 1px 離して sticky の背景の下に隠す。
      outlineOffset: '-2px',
    },
    // ドラッグ中はネイティブのテキスト選択帯を出さない (矩形選択と二重に見えるため)。
    '[data-range-dragging], [data-range-dragging] *': {
      userSelect: 'none',
    },
    // 押すと列を選べる列見出し (plan-common §2)。
    'th[data-range-column-header]': {
      cursor: 'pointer',
    },
    // tooltip 本文の中に textStyle を持つ子 (Text 等) を置いても行間を 1.6 に保つ
    // (UI 辞書 components/tooltip.md「本文の p / span / li も行間を継承し、各画面で再定義しない」)。
    '[data-scope="tooltip"][data-part="content"] :is(p, span, li)': {
      lineHeight: 'inherit!',
    },
    // 入力欄の placeholder は Text.disabled (UI 辞書 dictionary/tokens.md `--text-disabled`・
    // dictionary/components.md の素の input / textarea 行、 DS #1358)。 DS 0.14.2 の
    // Input / TextArea は component の prop で `_placeholder: { color: 'Text.lowEmphasis' }` を
    // 当てており、 prop 由来の宣言は `@layer` の外に出て globalCss (`@layer base`) に常に勝つため
    // `!` (= !important) で揃える。
    'input::placeholder, textarea::placeholder': {
      color: 'Text.disabled!',
    },
    'html, body': {
      fontFamily: ECLAIR_FONT_FAMILY,
    },
    // 色を持たない素の `a` を DS の `Link` と同じ色 (`Text.accentPrimary` = #0c00c5) にする。
    // DS 0.14.2 の globalCss は `a { color: 'teal.500' }` で、色を指定していないリンクだけが水色に
    // なり、DS の `Link` (Text.accentPrimary) と 2 色が並存していた。`Text.link` も 0.14.2 では
    // 旧値 #4164e0 で DS の Link と合わない (2026-10-15 ユーザー確認「リンク色は原則 DS 準拠」。
    // DS 0.18.5 の Link の仕様は Text.link = #0c00c5 / dark #b3bdfe。DS の更新で Text.link に戻す)。
    // globalCss は `@layer base` なので、recipe (Button を asChild で a にした時など) や
    // style / 色の props を持つ要素はそちらが勝つ。hover の下線は DS の globalCss のまま。
    a: {
      color: 'Text.accentPrimary',
    },
    body: {
      color: 'Text.highEmphasis',
    },
    // design SoT: `.ecl-modal-title { font-size: var(--font-size-xl); font-weight: 600 }` = 20px/600。
    // slotRecipe base は DS 側 recipe (@layer recipes) に負けるため、`!important` で強制適用する。
    // Chakra v3 の `!important` 記法: 値末尾に `!` を付与。
    '[data-scope="dialog"][data-part="title"]': {
      fontSize: '20px!',
      fontWeight: '600!',
    },
    // design SoT: 8080 `css/components.css` の `input[type="radio"]` (「DS RadioButton
    // 準拠」と明記) = `border: 2px solid var(--border-mid)`。実装は chakra `radiomark`
    // 既定の 1px が出ていた (2026-08-05 実測。色 #d9d9d9 とサイズ 16px は一致済み)。
    //
    // slotRecipe の base は DS 側 recipe (@layer recipes) に負けるため、dialog title /
    // alert gap と同じく globalCss + `!` (= !important) で強制する。
    // 選択時 (`data-state="checked"`) は除外する — 選択時の枠はドットのリングを
    // 兼ねており、太さを変えると見た目が変わるため。
    '[data-scope="radio-group"][data-part="item-control"]:not([data-state="checked"])':
      {
        borderWidth: '2px!',
      },
    // design SoT: 8080 `css/layout.css` の `.nav-flyout` =
    // `box-shadow: 0 0 32px rgba(28,50,92,0.07)` (コメントに「DS sideMenuShadow」と明記)。
    // DS `SideMenuLayoutMenuItem` の hover popup は `boxShadow: 'shadSm'`
    // (= 0 1px 2px rgba(0,0,0,0.02)) を直接指定しており、ほぼ視認できない。
    // éclair には サイドメニュー専用の `sideMenuShadow` トークンが存在するのに
    // 使われていないため、DS 側の実装漏れとみて app 側で上書きする (2026-08-05 実測)。
    //
    // popup は DS 内部の素の Box で data 属性も安定 class も持たないため、
    // TopMenu が popup に渡す content へ付けた `data-ebalance-flyout` (display:contents)
    // を `:has()` で辿って親 (= popup 本体) を指す。accordion 版 (モバイル幅で同じ
    // content が再描画される) は除外する。
    // DS が `sideMenuShadow` を使うようになったら本 override は不要になる。
    'div:not([class*="chakra-accordion"]):has(> div > [data-ebalance-flyout])':
      {
        boxShadow: 'sideMenuShadow!',
      },
    // popup ヘッダー下の区切り線。design (`.nav-flyout-header`) は
    // `border-bottom: 1px solid var(--border-mid)` (= #d9d9d9 / Border.midEmphasis) だが、
    // DS は `Border.lowEmphasis` (= #e0e0e0) と一段薄く、線が影のように滲んで見える
    // (2026-08-05 デザイナー指摘)。線は残したまま design と同じ濃さに揃える。
    // 対象は popup 直下の先頭 div = ヘッダー (本文は 2 番目)。
    'div:not([class*="chakra-accordion"]):has(> div > [data-ebalance-flyout]) > div:first-of-type':
      {
        borderBottomColor: 'Border.midEmphasis!',
      },
    // design SoT: `.ecl-alert { gap: var(--space-3) }` = 12px (Chromatic feedback-alert)。
    // data-scope/data-part セレクターが効かないため .chakra-alert__root クラスで直接上書き。
    '.chakra-alert__root': {
      gap: '12px!',
    },
    // 上の gap を 12px にしても、 children を渡した Alert では アイコン↔本文 が
    // **24px (= design の倍)** に見えていた (2026-08-18 デザイナー指摘)。
    //
    // 原因は gap の値ではなく **flex 子の数**。 DS `Alert` は content slot (Stack) を
    // 常に描画するが、 `title` / `description` prop ではなく children を渡すと
    // children は content slot の**中ではなく外側の兄弟**として並ぶ:
    //
    //   title/description 渡し … [icon][content stack(中身あり)]        → gap 1 回 = 12px
    //   children 渡し         … [icon][content stack(空)][children]     → gap 2 回 = 24px
    //
    // 本 app の Alert は 74 箇所が children 渡しで、 リッチな中身 (ボタン・複数行・
    // 監査情報) を持つものが多く title/description prop へは寄せられない。
    // そこで **空の content slot を畳んで** gap を 1 回に戻す。
    // 中身のある content slot (title/description 渡し) は `:empty` に該当しないので
    // 影響しない。
    //
    // `!` (= !important) は必須。 globalCss は `@layer base` に出るが、 content slot の
    // `display: flex` は chakra `Stack` が **非レイヤー**で出すため、 `!` 無しでは
    // 常に負けて畳まれない (dialog title / badge nowrap / radio 枠と同じ理由)。
    // Chromium 実測: `!` 無し = 空 slot が display:flex のまま残り アイコン↔本文 24px、
    // `!` 有り = display:none になり 12px。
    '.chakra-alert__root > .chakra-stack:empty': {
      display: 'none!',
    },
    // バッジ文言は折り返さない (2026-08-12 デザイナー指摘「約定経路の badge 文言は、
    // 改行されないようにしたい。badge の表示仕様は全画面共通」)。
    //
    // design SoT も全画面共通で nowrap: 8080 `css/components.css` の
    // `.badge { white-space: nowrap }`。chakra 既定の badge recipe も `white-space: nowrap` で、
    // DS `Badge` だけが component の prop で `whiteSpace: 'normal'` /
    // `overflowWrap: 'anywhere'` を明示して打ち消している (2026-08-12 実測)。
    // prop 由来の宣言は `@layer recipes` に常に勝つため recipe base では戻せず、
    // dialog title / alert gap と同じく globalCss + `!` (= !important) で design 値へ戻す。
    // `overflow-wrap: anywhere` は nowrap 下では行分割に効かないので触らない。
    //
    // 対象は DS `Badge` と `ChakraBadge` (= 既に nowrap なので変化なし) のみ。
    // Tag (`chakra-tag`) / Text は対象外。
    '.chakra-badge': {
      whiteSpace: 'nowrap!',
    },
    // DateRangePicker の popover 内タイトル (「期間を選択」) と カレンダーの年月見出し
    // (「2026年08月」) が 14px に落ちる問題の補正 (dsFallbackFixRecipes と同型)。
    //
    // DS の意図値: popover タイトル = textStyle `headerLg` (20px/600、dist:13632)、
    // 年月見出し = textStyle `headerMd` (16px/600、dist:10972)。
    // 実測値: DS の textStyles 定義 (dist:21489 / 21497) は fontSize が単位なしの
    // `"20"` / `"16"` で、chakra がそのまま通すため emotion が `font-size:20;` を
    // 出力 → CSS 的に不正で宣言ごと破棄され body の 14px を継承する
    // (2026-08-06 実測: 生成された class は `font-size:20` / `font-size:16`)。
    // font-weight / font-family / letter-spacing は有効な値なので当たっている →
    // ここで直すのは font-size だけ。
    // DS 自身もこの問題を認識しており Breadcrumb (dist:25117-25119) にだけ
    // 「chakra v3 では単位なし fontSize が解決されず font-size 宣言ごと落ちる」旨の
    // NOTE 付きで解決可能な token を併記している (= DateRangePicker / Calendar は
    // DS 側の対応漏れ)。DS が直ったら本 override は不要になる。
    //
    // design SoT も同値: 8080 `css/components.css` の `.ecl-drp-title` = 20px、
    // `.ecl-drp-month-title` = 16px (`css/tokens.css` の xl=20px / lg=16px)。
    // 日付セル (dist:11354 `fontSize: "1rem"`) と曜日行 (size 未指定 = 14px 継承) は
    // 既に正しいので触らない (= 「日付以外だけ小さい」の指摘どおり)。
    //
    // セレクターが構造依存なのは DS 内部の `Text` に data 属性も安定 class も無いため
    // (chakra v3 の Text は recipe を持たず className を出さない = 実測)。
    // `!` (= !important) は不要: 対象要素には有効な font-size 宣言が 1 つも残らず、
    // body からの継承に勝てばよい。
    // 適用範囲は DS Calendar を内包する popover のみ = DateRangePicker
    // (SupplyBilateral 受給期間フィルタ)。
    // `CompactCivilDateField` の DS `Calendar` は素の Box に直置き (popover scope 外)
    // なので本ルールの対象外 (別 issue)。
    '[data-scope="popover"][data-part="content"]:has([aria-label="Calendar navigation"]) > div > div:first-child p':
      {
        fontSize: 'xl',
      },
    '[data-scope="popover"][data-part="content"] [aria-label="Calendar navigation"] > p':
      {
        fontSize: 'lg',
      },
    // 同 popover の「選択をクリア」 も 12px に落ちる (2026-08-12 デザイナー指摘)。
    //
    // 原因は上 2 つと別。 このリンクは DS が `Link` を `as="button"` で出しており
    // (dist:13303-13311)、 **font-size 宣言を 1 つも持たない** (実測: class は
    // `chakra-link css-17xi72z` で font-size ルール無し)。 そのため祖先の
    // `[data-part="content"]` が持つ `font-size: sm` (= 12px) を継承する。
    // 隣の「キャンセル」「適用」 は Button recipe が `font-size: md` (14px) を
    // 明示するので落ちない → footer 内でクリアだけ小さい状態になっていた。
    //
    // design SoT も 14px: 8080 `css/components.css` の
    // `.ecl-drp-clear { font-size: var(--font-size-md) }` (`--font-size-md` = 14px)。
    //
    // popover 内の link は このクリアリンクのみなので `.chakra-link` で足りる。
    // カレンダーを内包する popover に限定して、 他の popover の link は触らない。
    '[data-scope="popover"][data-part="content"]:has([aria-label="Calendar navigation"]) .chakra-link':
      {
        fontSize: 'md',
      },
    // エラー状態の入力欄。 SoT は **DS (Chromatic `form-input--invalid`)** で、
    // `Input` component の実装値 `_invalid: { border: '1px solid {colors.Text.negativeMid}' }`
    // (= 1px / #d80004) + 地は `Surface.primary` のまま (塗り分けなし) が正。
    //
    // [DS差異] 8080 プロトの `.ecl-input.is-error` は
    // `border-color: var(--text-negative-high) !important` (#870103) + `background-color:
    // var(--surface-negative-low)` (#fff5f5) + `box-shadow: 0 0 0 1px ... inset` で、
    // 枠が濃く・地がピンク・実質 2px 相当になっており **DS 非準拠** (2026-08-06 デザイナー確認)。
    // プロト側の是正は別 issue。dev は DS に合わせる。
    //
    // ここで globalCss を当てる理由は色ではなく **経路**: DS `Input` は内部でもう一段
    // `Field.Root` を張り、`error` prop 未指定時は `invalid: false` になるため、呼び出し側の
    // `<FieldRoot invalid>` から降りてくる `data-invalid` が入力要素まで届かず、
    // DS 自身の `_invalid` が発火しない経路がある。外側 field root の `data-invalid` を
    // 起点に子孫へ直接当てて確実に 1px 赤枠を出す (内側 Field.Root の有無に依存しない)。
    // `!` = !important は dialog title / alert gap と同じ既存の手段。
    '[data-scope="field"][data-part="root"][data-invalid] :is(input, textarea, select)':
      {
        borderColor: 'Text.negativeMid!',
        borderWidth: '1px!',
      },
    // design SoT (`design/css/components.css:3612-3615`):
    // `@media (max-height: 640px) { .ecl-modal-backdrop { align-items: center; padding-top: 24px } }`
    // = 縦に余裕が無い端末だけ Modal の上寄せ (`placement="top"`) をやめ中央寄せへ戻す
    // (`design/CLAUDE.md`「縦が狭い端末 (`max-height: 640px`) だけ中央寄せへフォールバックする」)。
    // chakra の breakpoint は幅基準で max-height を表現できないため、placement のレスポンシブ
    // 指定ではなくここで media query を書く。 alignItems は placement variant (@layer recipes)
    // に負けるため title override と同じく `!` (= !important) が要る。
    // media query を外側に置くと globalCss の型 (SystemStyleObject) がセレクターを受けないため、
    // セレクターを外・media query を内側にする (出力 CSS は同じ)。
    '[data-scope="dialog"][data-part="positioner"]': {
      '@media (max-height: 640px)': {
        alignItems: 'center!',
        // design の `padding-top: 24px` 相当。 中央寄せでは content が縦に溢れた時に
        // margin が 0 になるため、最低余白は positioner 側の padding で確保する
        // (上下に付けて溢れた時の下端余白も揃える)。
        paddingBlock: '24px',
      },
    },
    '[data-scope="dialog"][data-part="content"]': {
      '@media (max-height: 640px)': {
        // placement="top" が content に入れる `--dialog-base-margin: spacing.16` (64px) を
        // placement="center" と同じ `auto` に戻す (content の margin-block は
        // `var(--dialog-margin, var(--dialog-base-margin))`)。
        // 固定値 (24px 等) にしてはいけない: positioner は `align-items: center` +
        // `overflow: auto` なので、 modal が viewport より高いと content の上端が
        // スクロール範囲外へ溢れてタイトル・閉じるボタンに到達できなくなる
        // (Chromium 実測: 620px 高 × content 1200px で上端 -290px / scrollHeight 934px)。
        // `auto` なら溢れた時に margin が 0 に潰れて全体がスクロールで追える。
        // `--dialog-margin` を上書きしないので size=full / cover の margin 0 も保たれる。
        '--dialog-base-margin': 'auto!',
      },
    },
  },
})

export const system = createSystem(defaultConfig, config, appConfig)
